// THE FILTER BUTTON on References and Rules (owner, 2026-09-11: "can we
// declutter by putting the search and filter functions inside a Filter button
// and pop-up? The button should indicate whether something is active").
//
// Every manager on those two screens opened with the same two controls sitting
// across the top — a 200px Status dropdown and a 280px search box — above a
// table whose first row was the thing anyone came to read. Two permanent
// controls per tab, ten tabs, and on most visits neither is touched.
//
// One button now, with the two controls behind it, and the button says what is
// on. Behind it is the centered Filters dialog since D-344 (owner, 2026-10-09:
// "The little thing in the corner is a pain to use"); it was a popover hung off
// the button until then. WHAT COUNTS AS ON is the part worth stating: a Status of anything other
// than "All" is a filter, so the default view — Status: Active — opens reading
// **Filter 1**, not a bare "Filter". That is the owner's own reading ("by
// default there would be one active filter, since we are filtering for active
// records"), and it is the honest one: the list on screen is NOT the whole
// table, and a control that looked idle while hiding rows is how someone
// concludes a record was deleted.
//
// The count is a number, not a dot, because the two filters are independent and
// "one of the two" is a different state from "both".
//
// IT IS AN ICON BUTTON NOW (owner, 2026-09-14), because it no longer sits in a
// toolbar of its own: References, Rules and Assignments moved their tools into
// the header's top-right cluster, where every control is a 44px glyph with a
// tooltip. So this matches `FilterButton`'s own `iconOnly` trigger exactly —
// brand fill when anything is narrowing the list, and the count carried in the
// tooltip, which is also the button's accessible name. The badge that used to
// ride inside the label went with the label: a number beside a glyph in a row
// of glyphs reads as a notification, not as a filter count.
//
// IT RENDERS ONE ELEMENT and nothing else (owner, 2026-09-11: "drop the
// 'Status: Active' that you have next to the button"). A summary line sat
// beside it for one round, naming the filters so the common case needed no
// click. It bought a click and cost a permanent second thing in the toolbar,
// which is what the button was introduced to remove — and the count already
// answers the question the toolbar has to answer, which is *whether* the list
// is narrowed. What is narrowing it is one click away, where the controls are.
import { useEffect, useId, useState } from 'react'
import {
  Button,
  Dropdown,
  Option,
  SearchBox,
  Tooltip,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { FilterRegular } from '@fluentui/react-icons'
import { DEFAULT_STATUS_FILTER, activeFilterCount, type StatusFilter } from '../lib/statusFilter'
import { StatusFilterDropdown } from './StatusFilterDropdown'
import { FilterDialog } from './FilterDialog'
import { MultiSelectField } from './MultiSelectField'
import { popupPositionProps, usePopupListboxClass } from './popupPositioning'

const useStyles = makeStyles({
  // EVERY CONTROL FILLS THE DIALOG (owner, 2026-09-11). Each brought its own
  // width from the toolbar it used to live in — the Status dropdown a fixed
  // 200px, the search box a 280px minimum — so inside a shared container they
  // sat at two different widths with a ragged right edge between them. The
  // popover decides the width now and both fill it, which is also why `surface`
  // above carries the minimum rather than either control.
  //
  // Reached through Fluent's class names because neither control takes a
  // `className` that lands on the element that is actually sized: the
  // Dropdown's own 200px is on its root, and `SearchBox` puts its minimum
  // there too.
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    '& .fui-Dropdown': { width: '100%', minWidth: 0 },
    '& .fui-SearchBox': { width: '100%', minWidth: 0, maxWidth: 'none' },
  },
  label: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
  },
  search: { width: '100%' },
})

/** One extra dropdown inside the popover, for a list that narrows on something
    besides Status — Assignments filters by role and by company (2026-09-14).
    The Status control stays its own prop rather than becoming one of these: it
    is the only filter ten lists share, it has its own labels and default, and
    its "Active counts as one" rule is pinned by a test.

    SINGLE-SELECT, kept only for a list with ONE narrowing option (owner,
    2026-10-14: every filter dropdown is multi-select unless there is a
    convincing reason not to be), where there is nothing to pick several of.
    Everything else is a `ListFilterMultiSelect` — two opposite options
    included, where ticking both is Any. */
interface ListFilterSelect {
  multiple?: false
  label: string
  value: string
  onChange: (v: string) => void
  /** `''` is the do-not-narrow option: listed first as "Any", and shown as
      grey "Any" help text while chosen (owner, 2026-10-09). Every other entry
      narrows. */
  options: readonly { value: string; label: string }[]
  /** What Reset returns this dropdown to. Defaults to `''` — the All option.

      Vehicles is the one list that needs it (owner, 2026-09-14): its Status is
      the fleet director's own three-way value, not our two-way `is_active`, so
      the equivalent of "Active is the default" is **In Service**. Without this
      its Reset would reveal every sold van, which is the opposite of what every
      other list's Reset does.

      IT DOES NOT AFFECT THE COUNT. The button counts anything but the All
      option, exactly as `activeFilterCount` counts `Status: Active` — "only All
      is no filter". This prop said "not narrowing" too until 2026-09-15, which
      made the Vehicles button read a bare **Filter** while In Service was
      hiding every Out of Service and Sold van: precisely the state that rule
      exists to prevent. */
  defaultValue?: string
}

/** A multi-select inside the dialog: a row passes when it matches ANY picked
    value, and an empty pick does not narrow (owner, 2026-10-14). */
interface ListFilterMultiSelect {
  multiple: true
  label: string
  values: readonly string[]
  onChange: (values: string[]) => void
  options: readonly { value: string; label: string }[]
  /** What Reset returns this list to. Defaults to empty. Like `defaultValue`,
      it does not affect the count: anything picked counts. */
  defaultValues?: readonly string[]
}

type ListFilterControl = ListFilterSelect | ListFilterMultiSelect

/** A control's applied value, in the one shape the draft holds. */
const valueOf = (sel: ListFilterControl): string | string[] => (sel.multiple ? [...sel.values] : sel.value)
/** True when the control narrows the list: a value other than All, or some but
    not every option picked — every option ticked is Any (owner, 2026-10-14). */
const narrows = (sel: ListFilterControl, v: string | readonly string[]) =>
  Array.isArray(v) ? v.length > 0 && v.length < sel.options.length : v !== ''

interface ListFiltersProps {
  /** Omit on a list with no status column — Vehicles, whose rows carry the
      fleet director's own three-way status and are not filtered on it. */
  status?: StatusFilter
  onStatusChange?: (v: StatusFilter) => void
  term: string
  onTermChange: (v: string) => void
  searchPlaceholder: string
  /** Names the list, so two Filter buttons in one session are distinguishable
      to a screen reader. **Title Case** (owner, 2026-09-14): it is interpolated
      into this button's tooltip, which is also its accessible name, and every
      other tooltip in the header cluster titles its words — Upload, User
      Guide, Custom Notifications, Add Reference, Import. */
  ariaLabel: string
  /** Extra dropdowns, above the search box in the order given. */
  selects?: readonly ListFilterControl[]
}

export function ListFilters({
  status,
  onStatusChange,
  term,
  onTermChange,
  searchPlaceholder,
  ariaLabel,
  selects = [],
}: ListFiltersProps) {
  const s = useStyles()
  const searchId = useId()
  const selectIdBase = useId()
  const popupListbox = usePopupListboxClass()
  const [open, setOpen] = useState(false)
  // Each extra dropdown that is not on its All option counts — the same rule
  // `activeFilterCount` applies to Status ("only All is no filter"), so the
  // button's number always means "this many things are hiding rows from you".
  // NOT compared against `defaultValue`: a default that narrows still narrows.
  const count =
    activeFilterCount(status, term) + selects.filter((sel) => narrows(sel, valueOf(sel))).length
  // Names the list as well as the count, because several of these buttons can
  // exist in one session and the tooltip is now the only text the control has.
  const label = count > 0 ? `Filter ${ariaLabel} (${count})` : `Filter ${ariaLabel}`

  // THE DRAFT (owner, 2026-10-09: "Setting should not be applied unless the
  // Apply button is pressed"): what the dialog edits, re-seeded from the applied
  // values each time it opens, so closing it any other way discards the edits.
  const applied = { status, term, values: selects.map(valueOf) }
  const [draft, setDraft] = useState(applied)
  const appliedKey = JSON.stringify(applied)
  useEffect(() => {
    if (open) setDraft(JSON.parse(appliedKey) as typeof applied)
  }, [open, appliedKey])

  function apply() {
    if (draft.term !== term) onTermChange(draft.term)
    if (draft.status !== undefined && JSON.stringify(draft.status) !== JSON.stringify(status)) {
      onStatusChange?.(draft.status)
    }
    selects.forEach((sel, i) => {
      const next = draft.values[i]
      if (sel.multiple) {
        const picked = Array.isArray(next) ? next : []
        if (JSON.stringify(picked) !== JSON.stringify(sel.values)) sel.onChange(picked)
      } else if (next !== sel.value) {
        sel.onChange(typeof next === 'string' ? next : '')
      }
    })
  }

  return (
    <>
      <Tooltip content={label} relationship="label">
        <Button appearance={count > 0 ? 'primary' : 'secondary'} icon={<FilterRegular />} onClick={() => setOpen(true)} />
      </Tooltip>
      <FilterDialog
        open={open}
        onClose={() => setOpen(false)}
        // Reset returns the draft to the DEFAULT view, not to an unfiltered
        // one: Status: Active is what every one of these lists opens on, and a
        // reset that revealed every retired row would be a different action
        // wearing the same word. Apply then commits it.
        onReset={() =>
          setDraft({
            status: status === undefined ? undefined : DEFAULT_STATUS_FILTER,
            term: '',
            values: selects.map((sel) => (sel.multiple ? [...(sel.defaultValues ?? [])] : (sel.defaultValue ?? ''))),
          })
        }
        onApply={apply}
      >
        {(host) => (
          <>
            {draft.status !== undefined && onStatusChange && (
              <div className={s.field}>
                <span className={s.label}>Status</span>
                <StatusFilterDropdown
                  value={draft.status}
                  onChange={(v) => setDraft((prev) => ({ ...prev, status: v }))}
                  popupHost={host}
                />
              </div>
            )}
            {selects.map((sel, i) => {
              if (sel.multiple) {
                const picked = draft.values[i]
                // The heading is this dialog's own small label, as on Status
                // and Search, rather than the field's larger built-in one.
                return (
                  <div className={s.field} key={sel.label}>
                    <span className={s.label}>{sel.label}</span>
                    <MultiSelectField
                      popupHost={host}
                      label=""
                      ariaLabel={sel.label}
                      options={sel.options}
                      selected={Array.isArray(picked) ? picked : []}
                      onChange={(next) =>
                        setDraft((prev) => ({
                          ...prev,
                          values: prev.values.map((v, j) => (j === i ? next : v)),
                        }))
                      }
                    />
                  </div>
                )
              }
              const current = draft.values[i]
              const value = typeof current === 'string' ? current : ''
              return (
                <div className={s.field} key={sel.label}>
                  <label className={s.label} htmlFor={`${selectIdBase}-${i}`}>
                    {sel.label}
                  </label>
                  {/* EMPTY READS "Any", in the help-text grey, and "Any" is the
                      option that clears it (owner, 2026-10-09: every filter
                      dropdown takes the same approach to an empty field). */}
                  <Dropdown
                    id={`${selectIdBase}-${i}`}
                    {...popupPositionProps(host)}
                    listbox={{ className: popupListbox }}
                    placeholder="Any"
                    selectedOptions={value === '' ? [] : [value]}
                    value={sel.options.find((o) => o.value === value)?.label ?? ''}
                    onOptionSelect={(_, d) =>
                      setDraft((prev) => ({
                        ...prev,
                        values: prev.values.map((v, j) => (j === i ? (d.optionValue ?? '') : v)),
                      }))
                    }
                  >
                    <Option value="">Any</Option>
                    {sel.options
                      .filter((o) => o.value !== '')
                      .map((o) => (
                        <Option key={o.value} value={o.value} text={o.label}>
                          {o.label}
                        </Option>
                      ))}
                  </Dropdown>
                </div>
              )
            })}
            <div className={s.field}>
              <label className={s.label} htmlFor={searchId}>
                Search
              </label>
              <SearchBox
                id={searchId}
                className={s.search}
                placeholder={searchPlaceholder}
                value={draft.term}
                onChange={(_, d) => setDraft((prev) => ({ ...prev, term: d.value }))}
              />
            </div>
          </>
        )}
      </FilterDialog>
    </>
  )
}
